import { APP_INITIALIZER, ErrorHandler, NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";

import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { PaginationModule } from "ngx-bootstrap/pagination";
import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { TranslateModule, TranslateLoader } from "@ngx-translate/core";
import { ToastrModule } from "ngx-toastr";
import { FormService } from "./services/form.service";
import { HelperService } from "./services/helper.service";
import { LocalStorageService } from "./services/local-storage.service";
import { GlobalErrorHandler } from "./services/globalErrorHandler";
import { HeaderLayoutService } from "./pages/shared/layout/layout.service";
import { BsModalRef, ModalModule } from "ngx-bootstrap/modal";
import { DatePipe } from "@angular/common";
import { TransactionStatusPipe } from "./pipes/transaction-status.pipe";
import { SharedModule } from "./pages/shared/shared.module";
import { SuccessComponentComponent } from "./pages/shared/components/success-component/success-component.component";
import { NgIdleKeepaliveModule } from "@ng-idle/keepalive";
import { EncryptionService } from "./services/encryption.service";
import * as Sentry from "@sentry/angular";
import { Router } from "@angular/router";
import { initializeApp } from "firebase/app";
import { getMessaging } from "firebase/messaging";
import { environment } from "src/environments/environment";
import { FoodService } from "./services/merchant/food.service.base";
import { FoodOrderService } from "./services/merchant/food-order.service.base";
import { GoogleMapService } from "./services/google-map.service";
import { ApiFoodService } from "./services/merchant/food.service.api";
import { FoodOrderServiceApi } from "./services/merchant/food-order.service.rest";
import { ProductService } from "./pages/merchant/tour/product.service";
import { ProductServiceIplm } from "./pages/merchant/tour/product.service.rest";
import { NgxSpinnerModule } from "ngx-spinner";
import { MomentModule } from "ngx-moment";
import { AngularFireModule } from "@angular/fire/compat";
import { AngularFireMessagingModule } from "@angular/fire/compat/messaging";
import { FirebaseService } from "./services/firebase.service";
import { TimeagoModule } from "ngx-timeago";
import { RestaurantSelectionService } from "./services/restaurant-selection.service";

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, "../assets/i18n/", ".json");
}

// Initialize Firebase
const app = initializeApp(environment.firebase);
const messaging = getMessaging(app);

@NgModule({
  declarations: [AppComponent, SuccessComponentComponent],
  exports: [PaginationModule, NgxSpinnerModule],
  bootstrap: [AppComponent],
  imports: [
    SharedModule,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot(),
    PaginationModule.forRoot(),
    ModalModule.forRoot(),
    NgxSpinnerModule.forRoot({ type: "ball-scale-multiple" }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    NgIdleKeepaliveModule.forRoot(),
    MomentModule,
    AngularFireMessagingModule,
    AngularFireModule.initializeApp(environment.firebase),
    TimeagoModule.forRoot(),
  ],
  providers: [
    DatePipe,
    TransactionStatusPipe,
    FormService,
    HelperService,
    HeaderLayoutService,
    GoogleMapService,
    LocalStorageService,
    EncryptionService,
    GlobalErrorHandler,
    BsModalRef,
    FirebaseService,
    {
      provide: FoodService,
      useClass: ApiFoodService,
    },
    {
      provide: FoodOrderService,
      useClass: FoodOrderServiceApi,
    },
    {
      provide: ProductService,
      useClass: ProductServiceIplm,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi()),
    RestaurantSelectionService,
  ],
})
export class AppModule {}
